/**
 * SHOP-1 — the Launch Edition catalog: every hoodie, patch and pin, once.
 *
 * Rennan's line (the "Nova Launch Edition" design): twelve hoodies,
 * twenty embroidered mission patches and five hard-enamel pins, built
 * around Nova and the Orbit program. Sold as pre-orders in the shop
 * (/shop) and offered free — one of each — to Founding Members
 * (/founding/:token, which reads this same catalog through
 * src/config/founding.ts).
 *
 * NAMES AND WORDS
 * ---------------
 * Every product has its own name and its own description (Sep 28, per
 * Rennan: "come up with names and unique descriptions for all the
 * products including the items on the founding members items").
 * `detail` keeps the design sheet's construction line — size, twill,
 * edge — and the hoodie `code` is the design's number on that sheet.
 * Where a patch is embroidered with words, its name is those words.
 *
 * IDS ARE FOREVER
 * ---------------
 * `id` is what a Founding Member's pick and a shop order store. Never
 * rename or reuse one. Stripe line items carry it as `sku`, and the
 * server keeps its own copy of the ids and prices in
 * supabase/functions/_shared/merchCatalog.ts — the price a customer
 * pays is the server's, never the browser's. Change a price there AND
 * here.
 *
 * PICTURES
 * --------
 * public/founding/kit/<id>.webp (large), <id>-sm.webp (tile),
 * <id>-back.webp (hoodie back print). Every patch shares
 * patch-back.webp (the iron-on backing) and every pin pin-back.webp.
 * All rendered straight from the design file.
 */

export type MerchKind = "hoodie" | "patch" | "pin";

export interface MerchItem {
  id: string;
  kind: MerchKind;
  /** The product's own name. */
  name: string;
  /** Hoodies: the fleece colour. */
  colorway?: string;
  /** Hoodies: the design number on the Launch Edition sheet. */
  code?: string;
  /** One or two sentences of its own. */
  description: string;
  /** Construction, from the design sheet. */
  detail: string;
  /** USD. The server's copy (merchCatalog.ts) is what's charged. */
  price: number;
  image: string;
  thumb: string;
  /** The reverse: a hoodie's back print, a patch's backing, a pin's back. */
  back: string;
}

const img = (file: string) => `/founding/kit/${file}.webp`;

export const MERCH_PRICES: Record<MerchKind, number> = { hoodie: 99.99, patch: 19.99, pin: 14 };

/** The shop's spec lines — shared by every item of a kind. */
export const MERCH_SPECS: Record<MerchKind, string> = {
  hoodie:
    "Heavyweight 400 gsm brushed-back fleece · oversized drop-shoulder fit · kangaroo pocket · ribbed cuffs and hem · water-based screen print · tiny Orbit or Nova mark at the cuff · unisex XS–3XL",
  patch:
    "Embroidered on cotton twill · satin-stitch fill · merrowed rope edge · iron-on heat-seal backing · numbered run",
  pin: "Die-struck hard enamel · polished silver plating · raised metal lines · double butterfly clutch",
};

function hoodie(code: string, name: string, colorway: string, description: string, detail: string): MerchItem {
  const id = `hoodie-${code.toLowerCase()}`;
  return {
    id,
    kind: "hoodie",
    name,
    colorway,
    code,
    description,
    detail,
    price: MERCH_PRICES.hoodie,
    image: img(id),
    thumb: img(`${id}-sm`),
    back: img(`${id}-back`),
  };
}

function patch(id: string, name: string, description: string, detail: string): MerchItem {
  return {
    id,
    kind: "patch",
    name,
    description,
    detail,
    price: MERCH_PRICES.patch,
    image: img(id),
    thumb: img(`${id}-sm`),
    back: img("patch-back"),
  };
}

function pin(id: string, name: string, description: string, detail: string): MerchItem {
  return {
    id,
    kind: "pin",
    name,
    description,
    detail,
    price: MERCH_PRICES.pin,
    image: img(id),
    thumb: img(`${id}-sm`),
    back: img("pin-back"),
  };
}

export const MERCH_HOODIES: MerchItem[] = [
  hoodie(
    "H1",
    "Apogee",
    "Heather grey",
    "One orbit drawn in a single line across the back — her eyes at the center, one orange ball riding the high point. The front stays quiet: a small Nova Intelligence mark on the chest, another at the cuff.",
    "Front: Nova Intelligence mark, left chest and cuff · Back: single-line orbit, her eyes, one orange ball",
  ),
  hoodie(
    "H3",
    "Mast",
    "Heather grey",
    "Her antenna runs the full height of the back — stem, ring, orange ball — like a radio mast against a night sky. Nova Intelligence on the chest; a tiny Orbit mark rides the cuff.",
    "Front: Nova Intelligence mark, left chest; Orbit mark at the cuff · Back: her antenna at full height",
  ),
  hoodie(
    "H4",
    "Pennant",
    "Lunar teal",
    "Deep lunar teal with her ivory eyes over the heart. Around back, a pennant in fine ivory line with her eyes inside and one orange rule tracing the edge.",
    "Front: ivory eyes, left chest; Orbit mark at the cuff · Back: line-art pennant, one orange inner rule",
  ),
  hoodie(
    "H8",
    "Scanline",
    "Ivory",
    "Ivory fleece and the Orbit script, small, on the chest. The back sets the logo over her happy eyes, with LCD scan lines running through the ink — a screen caught mid-refresh.",
    "Front: Orbit script, left chest; Nova Intelligence mark at the cuff · Back: logo over her happy eyes, scan-lined ink",
  ),
  hoodie(
    "H9",
    "Broadside",
    "Orbit orange",
    "Launch-pad orange. A deep-space Nova Intelligence mark on the chest, and the Orbit retro script across the full width of the back — the loudest piece in the collection, on purpose.",
    "Front: Nova Intelligence mark in deep space, left chest; Orbit mark at the cuff · Back: Orbit retro script, full width",
  ),
  hoodie(
    "H10",
    "Double Take",
    "Heather grey",
    "Her idle eyes on the front, LCD-lined and dead center. Turn around and they're wide awake — big happy eyes over the Nova Intelligence logo. Everyone looks twice.",
    "Front: idle eyes, center chest; Orbit mark at the cuff · Back: happy eyes over the Nova Intelligence logo",
  ),
  hoodie(
    "H16",
    "Broadcast",
    "Orbit orange",
    "Idle eyes on the chest. On the back, her LED ball sends rings of signal out in every direction — for anyone who's ever gone live.",
    "Front: idle eyes, left chest · Back: the LED ball broadcasting in concentric rings",
  ),
  hoodie(
    "H17",
    "One Line",
    "Deep space",
    "Near-black and nearly silent. A single continuous line draws her head and antenna on the chest; the back says only NOVA · ORBIT, tiny, at the nape. For the ones who notice.",
    "Front: one-line head and antenna, left chest · Back: NOVA · ORBIT at the nape",
  ),
  hoodie(
    "H20",
    "Terminal",
    "Red",
    "One line of code, small and centered on the chest. The back prints a short terminal exchange in her voice — the kind of thing people read over your shoulder in the coffee line.",
    "Front: a single line of code, center chest; Orbit mark at the cuff · Back: a terminal exchange in her voice",
  ),
  hoodie(
    "H21",
    "Manifest",
    "Navy",
    "A flight label, stacked small on the chest — line-art head, NOVA, two taglines, 00 and hazard stripes. The back is the full manifest: orange block, dates, geometric marks and a barcode.",
    "Front: label stack, left chest; Orbit mark at the cuff · Back: full label with orange block, dates, hazard stripe and barcode",
  ),
  hoodie(
    "H22",
    "Blueprint",
    "Mustard",
    "Line-art head on the chest. The back is a working drawing — grid, callouts and a spec panel — with Nova drafted as a technical figure, ready for the build.",
    "Front: line-art head, left chest; Orbit mark at the cuff · Back: blueprint grid, callouts and spec panel",
  ),
  hoodie(
    "H24",
    "Liftoff",
    "Grey",
    "A ringed-planet eyes mark, small on the chest. On the back, TO THE ORBIT arches over her antenna lifting off on a plume of orange.",
    "Front: ringed-planet eyes mark, left chest; Orbit mark at the cuff · Back: TO THE ORBIT arch, antenna lifting off",
  ),
];

export const MERCH_PATCHES: MerchItem[] = [
  patch(
    "patch-nova-intelligence-seal",
    "First Contact",
    "Happy Nova on ivory satin, ringed in bold NOVA INTELLIGENCE type on Orbit orange twill. The patch for the day you two said hello.",
    "3.5 in round · Orbit orange twill, deep-space rope edge · happy Nova on ivory satin",
  ),
  patch(
    "patch-wordmark-bars",
    "Callsign",
    "Two bars, one mark: the Nova Intelligence logo in ivory and in deep space. Wear them as a pair, or keep one and give the other away.",
    "Two 3.5 × 1.6 in bars · ivory and deep-space colorways · tone-on-tone edge",
  ),
  patch(
    "patch-creator-corps",
    "Creator Corps",
    "Nova over a lunar horizon, ringed by the unit's creed — Shoot · Edit · Deliver. For the ones who do all three before lunch.",
    "3.5 in round · mustard rope edge, deep-space field · full Nova over a lunar horizon",
  ),
  patch(
    "patch-orbit-insignia",
    "Vector",
    "The Orbit retro script on lunar teal, crossed by an orange vector and circled in an ivory orbit ring. Our take on the space-age insignia.",
    "3.5 in round · lunar teal, ivory orbit ring, orange vector · Orbit retro logo",
  ),
  patch(
    "patch-she-notices-things",
    "Observer",
    "Just her two eyes on ivory twill, and nothing else. She notices things — now so will everyone who sees your bag.",
    "3.5 × 2.8 in oval · ivory twill, deep-space rope edge · her two eyes only",
  ),
  patch(
    "patch-rocker-pair",
    "Rockers",
    "SHE NOTICES over THINGS, on two rocker bars in orange and deep space. Stack them like a unit tab, or split them across a sleeve.",
    "Two 3.5 × 1 in bars · orange + deep space",
  ),
  patch(
    "patch-antenna-crew",
    "Antenna Crew",
    "A hexagon crest with her antenna standing tall, stamped UNIT · 01. Crew issue, first run.",
    "3.5 in hexagon · darker teal twill, mustard rope · her antenna as the crest",
  ),
  patch(
    "patch-display",
    "Standby",
    "The CRT with her happy eyes lit, coral cheeks glowing just outside the bezel. Powered on and ready when you are.",
    "3.2 × 2.4 in rounded · the CRT with happy eyes, coral cheeks outside the bezel",
  ),
  patch(
    "patch-orbit-mission-diamond",
    "Orbit Mission",
    "Her eyes inside the orbit ring on an ivory diamond, edged in orange rope and dated 2026. The mission patch, first flight.",
    "3.5 in diamond · ivory twill, orange rope · eyes inside the orbit ring",
  ),
  patch(
    "patch-orbit-seal",
    "Established",
    "The Orbit script on mustard twill with EST. IN ORBIT underneath — the founding seal, for the jacket that's been with you since the start.",
    "3 in round · mustard twill, deep-space rope · Orbit retro logo",
  ),
  patch(
    "patch-shell-pennant",
    "Shell",
    "Her two-piece shell drawn in ivory line on lunar teal, cut as a pennant. Technical, minimal, quietly nerdy.",
    "3.2 × 3 in pennant · lunar teal, ivory rope · two-piece shell line art",
  ),
  patch(
    "patch-orbit-mission-shield",
    "Mission Shield",
    "Nova over an ivory lunar horizon on navy twill, with an orange inner rule and SHE NOTICES THINGS across the base. The dress-uniform patch.",
    "3.5 in shield · navy twill, ivory rope, orange inner rule · Nova over an ivory lunar horizon",
  ),
  patch(
    "patch-out-of-this-orbit",
    "Out of This Orbit",
    "Her LED ball beaming down from a two-tone pentagon — deep space up top, ivory below. Exactly what it says.",
    "3.5 in pentagon · two-tone deep space + ivory · her LED ball beaming down",
  ),
  patch(
    "patch-space-triangle",
    "Delta",
    "A ringed LED planet set inside a two-tone triangle. Clean geometry for a strap, a cap or a lens case.",
    "3.5 in triangle · two-tone · ringed LED planet",
  ),
  patch(
    "patch-space-race",
    "Space Race",
    "Her antenna launches as the rocket through a deep-space arch, SPACE RACE on the banner below. Everyone's racing — she's already up there.",
    "3 × 3.5 in arch · ivory rope, deep-space field · antenna launching as the rocket",
  ),
  patch(
    "patch-drip-insignia",
    "Meltdown",
    "The NOVA insignia melting in orange and mustard drips over lunar teal. For deadline week.",
    "3.5 in round · lunar teal, orange + mustard drip, ivory rope · NOVA insignia melting",
  ),
  patch(
    "patch-explorer-diamond",
    "Explorer",
    "Happy Nova tucked inside a ringed planet, an EXPLORER banner across a two-tone diamond. For the ones who go looking for the light.",
    "3.5 in diamond · two-tone · happy Nova inside the ringed planet, banner across",
  ),
  patch(
    "patch-she-notices-things-arch",
    "Window Seat",
    "Full Nova floating in a deep-space window above an orange band. She always takes the window seat.",
    "3 × 3.5 in arch · deep-space window, orange band · full Nova floating",
  ),
  patch(
    "patch-outer-orbit-seal",
    "Outer Orbit",
    "OUTER ORBIT in two-tone ring type, with her antenna rising through the center like a rocket on the pad.",
    "3.5 in round · two-tone ring type · antenna as the rocket",
  ),
  patch(
    "patch-moon-camp",
    "Moon Camp",
    "Idle Nova parked on an ivory moon inside a mustard-roped octagon. Base camp for the long shoot.",
    "3.5 in octagon · darker teal, mustard rope · idle Nova on the ivory moon",
  ),
];

export const MERCH_PINS: MerchItem[] = [
  pin(
    "pin-happy-nova",
    "Hello World",
    "Nova head to toe in hard enamel — antenna up, cheeks pink, mid-smile. She says hi to everyone you meet.",
    "1.25 in · hard enamel, polished silver",
  ),
  pin(
    "pin-idle-eyes",
    "Idle Mode",
    "Her idle eyes in signal blue on a deep-space screen, scan lines engraved into the metal. Quiet, watching, listening.",
    "1.25 in wide · deep-space enamel, signal-blue eyes, engraved scan lines",
  ),
  pin(
    "pin-orbit-ring",
    "Satellite",
    "An ivory ring passing in front of her eyes, with an orange ball at apogee. The whole Orbit idea in an inch and a half.",
    "1.5 in wide · ivory ring passes in front, orange ball at apogee",
  ),
  pin(
    "pin-orbit-logo",
    "Signature",
    "The Orbit script, die-cut to every loop and polished to a mirror. Signed, sealed, pinned.",
    "1.25 in wide · die-cut to the script, polished silver plate",
  ),
  pin(
    "pin-line-head",
    "Wireframe",
    "Her head and antenna as cut-out silver line, with a single orange enamel dot at the top. Nothing extra.",
    "1.25 in · cut-out die-struck silver, one orange enamel dot",
  ),
];

export const MERCH: MerchItem[] = [...MERCH_HOODIES, ...MERCH_PATCHES, ...MERCH_PINS];

export const merchById = (id: string): MerchItem | undefined => MERCH.find((m) => m.id === id);

/** "Manifest · H21" for a hoodie, the name for anything else. */
export const merchTitle = (m: Pick<MerchItem, "name" | "code">) => (m.code ? `${m.name} · ${m.code}` : m.name);

export const HOODIE_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"] as const;
export type HoodieSize = (typeof HOODIE_SIZES)[number];
